function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useMemo, useState, useEffect } from 'react';
import { arrayOf, bool, func, instanceOf, number, object, oneOf, string, oneOfType } from 'prop-types';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import Popover from '@mui/material/Popover';
import { DateRange as Calendar, KeyboardArrowLeft as Left, KeyboardArrowRight as Right } from 'mosaic-react-icons';
import { SovosMenuItem, SovosButton, SovosIconButton, SovosTooltip } from 'mosaic-react';
import Iterator from '../../../utils/yearIterator'; // Use State Use Effect

const Months = [];
const Root = styled('div')({
  height: '100%',
  width: 'fit-content'
});
const Widget = styled('div')({
  alignItems: 'center',
  display: 'flex',
  height: '100%'
});
const SelectionButton = styled(SovosButton)(({
  theme: {
    palette,
    spacing,
    typography
  }
}) => ({
  ...typography.body1,
  borderRadius: 0,
  height: '100%',
  justifyContent: 'flex-start',
  minWidth: spacing(15),
  padding: `0 ${spacing()}`,
  '&.MuiButton-textPrimary': {
    color: palette.text.primary,
    '&:hover': {
      backgroundColor: palette.action.hover
    },
    '&:disabled': {
      color: palette.action.disabled
    }
  }
}));
const CalendarIcon = styled(Calendar)(({
  theme: {
    spacing
  }
}) => ({
  paddingRight: spacing(),
  height: spacing(3),
  width: spacing(4)
}));
const DropdownContents = styled('div')(({
  theme: {
    palette,
    spacing,
    typography
  }
}) => {
  const selectedStyle = {
    backgroundColor: palette.text.primary,
    color: palette.primary.contrastText
  };
  return {
    display: 'flex',
    width: 200,
    padding: spacing(),
    '& .sovosMonthSelector__months': {
      paddingRight: spacing(),
      flex: 1
    },
    '& .sovosMonthSelector__years': {
      borderLeft: `1px solid ${palette.divider}`,
      paddingLeft: spacing(),
      flex: 1
    },
    '& .sovosMonthSelector__item': {
      ...typography.body1,
      lineHeight: spacing(4),
      height: spacing(4),
      padding: [[0, spacing()]],
      minHeight: spacing(4),
      zIndex: 999
    },
    '& .sovosMonthSelector__item--selected': {
      '&.MuiButtonBase-root': {
        ...selectedStyle,
        '&:hover': selectedStyle
      }
    }
  };
});
const SovosMonthSelector = ({
  className,
  'data-testid': dataTestId,
  disabled,
  format,
  years,
  minDate,
  maxDate,
  monthsAbbreviated,
  monthsFull,
  nextMonthTooltipText,
  onChange,
  onToggle,
  previousMonthTooltipText,
  selectedMonth: initialMonth,
  selectedYear: initialYear,
  specificMonthTooltipText,
  style,
  thisMonthLabel,
  yearSort,
  ...rest
}) => {
  const [month, year] = useMemo(() => {
    const minYear = minDate.getFullYear();
    const minMonth = minDate.getMonth();
    const maxYear = maxDate.getFullYear();
    const maxMonth = maxDate.getMonth();
    let yr = initialYear;
    let mth = initialMonth;
    if (yr < minYear) {
      yr = minYear;
      mth = minMonth;
    } else if (yr === minYear && mth < minMonth) {
      mth = minMonth;
    } else if (yr > maxYear) {
      yr = maxYear;
      mth = maxMonth;
    } else if (yr === maxYear && mth > maxMonth) {
      mth = maxMonth;
    }
    return [mth, yr];
  }, [initialMonth, initialYear, minDate, maxDate]);
  let newMonth = "";
  const [anchorElement, setAnchorElement] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const getOutput = (newMonth, newYear) => {
    return {
      month: 0,
      year: newYear
    };
  };
  const updateYear = newYear => {
    if (onChange) {
      onChange(getOutput(0, newYear));
    }
  };
  useEffect(() => {
    setSelectedYear(initialYear);
  }, [initialYear]);
  let yearsIterator = new Iterator(years, initialYear);
  const closeMenu = () => {
    setShowMenu(false);
    onToggle(false);
  };
  const openMenu = event => {
    setAnchorElement(event.currentTarget);
    setShowMenu(true);
    onToggle(true);
  };
  useEffect(() => {
    yearsIterator.updateYear(years, selectedYear);
  }, [selectedYear]);
  const renderYears = () => {
    if (yearSort === 'desc') {
      years.reverse();
    }
    return years.map(item => {
      const isSelected = item === selectedYear;
      return /*#__PURE__*/React.createElement(SovosMenuItem, {
        className: clsx(`sovosMonthSelector__year--${item}`, 'sovosMonthSelector__item', isSelected && 'sovosMonthSelector__item--selected'),
        key: `year-${item}`,
        onClick: () => {
          setSelectedYear(item);
          updateYear(item);
          setShowMenu(false);
        }
      }, item);
    });
  };
  const renderDropdownMenu = () => /*#__PURE__*/React.createElement(Popover, {
    open: showMenu,
    anchorEl: anchorElement,
    anchorOrigin: {
      horizontal: 'left',
      vertical: 'bottom'
    },
    onClose: closeMenu,
    className: "sovosMonthSelector__popover"
  }, /*#__PURE__*/React.createElement(DropdownContents, null, /*#__PURE__*/React.createElement("div", {
    className: "sovosMonthSelector__years"
  }, renderYears())));
  const renderWidget = () => {
    const label = selectedYear;
    return /*#__PURE__*/React.createElement(Widget, null, /*#__PURE__*/React.createElement(SovosTooltip, {
      title: specificMonthTooltipText
    }, /*#__PURE__*/React.createElement(SelectionButton, {
      disabled: disabled,
      className: "sovosMonthSelector__selectButton",
      onClick: openMenu,
      variant: "text"
    }, /*#__PURE__*/React.createElement(CalendarIcon, null), label)), /*#__PURE__*/React.createElement(SovosIconButton, {
      className: "sovosMonthSelector__previousButton",
      onClick: () => {
        const currentYear = yearsIterator.previous();
        setSelectedYear(currentYear);
        updateYear(currentYear);
      },
      tooltipText: previousMonthTooltipText,
      disabled: !yearsIterator.hasPrevious() || disabled,
      size: "small"
    }, /*#__PURE__*/React.createElement(Left, null)), /*#__PURE__*/React.createElement(SovosIconButton, {
      className: "sovosMonthSelector__nextButton",
      onClick: () => {
        const currentYear = yearsIterator.next();
        setSelectedYear(currentYear);
        updateYear(currentYear);
      },
      tooltipText: nextMonthTooltipText,
      disabled: !yearsIterator.hasNext() || disabled,
      size: "small"
    }, /*#__PURE__*/React.createElement(Right, null)));
  };
  return /*#__PURE__*/React.createElement(Root, _extends({
    className: clsx('sovosMonthSelector', className),
    "data-testid": dataTestId,
    style: style
  }, rest), renderWidget(), renderDropdownMenu());
};
SovosMonthSelector.propTypes = {
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * @ignore
   */
  'data-testid': string,
  /**
   * If `true`, the button is disabled
   */
  disabled: bool,
  /**
   * Formats the date
   */
  format: oneOf(['number', 'month-name']),
  /**
   * The minimum allowed date
   */
  minDate: instanceOf(Date),
  /**
   * The maximun allowed date
   */
  maxDate: instanceOf(Date),
  /**
   * Abbreviated months for the months menu
   */
  monthsAbbreviated: arrayOf(string),
  /**
   * Array of the months belonging to the selected year
   */
  monthsFull: arrayOf(string),
  /**
   * Next month tooltip text
   */
  nextMonthTooltipText: string,
  /**
   * Callback fired when the input value is changed `function(value:
   * {month: number|string, year: number}) => void`
   */
  onChange: func,
  /**
   * Callback fired when the menu is open or closed `function(open: bool) => void`
   */
  onToggle: func,
  /** 
   * @description List of years to be rendered
   * @type {arrayOf(number|string)}
   */
  years: oneOfType([arrayOf(string), arrayOf(number)]),
  /**
   * Month picked
   */
  selectedMonth: number,
  /**
   * Year picked
   */
  selectedYear: number,
  /**
   * Inline styles applied to the root element
   */
  style: object,
  /**
   * Selected month tooltip text
   */
  specificMonthTooltipText: string,
  /**
   * Previous month tooltip text
   */
  previousMonthTooltipText: string,
  /**
   * Current Month
   */
  thisMonthLabel: string,
  /**
   * Sort direction for years, ascending or descending
   */
  yearSort: oneOf(['asc', 'desc'])
};
SovosMonthSelector.defaultProps = {
  'data-testid': undefined,
  disabled: false,
  onToggle: () => {},
  onChange: () => {},
  years: [],
  minDate: new Date(new Date().getFullYear(), 0),
  maxDate: new Date(new Date().getFullYear(), 11),
  selectedYear: new Date().getFullYear(),
  selectedMonth: new Date().getMonth(),
  format: 'number',
  className: "",
  style: {},
  monthsAbbreviated: Months?.monthsAbbreviated,
  monthsFull: Months?.monthsFull,
  specificMonthTooltipText: undefined,
  previousMonthTooltipText: undefined,
  nextMonthTooltipText: undefined,
  thisMonthLabel: undefined,
  yearSort: 'asc'
};
export default SovosMonthSelector;